<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Packages" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4">
          <form class="position-relative table-src-form me-0" @submit.prevent>
            <input type="text" class="form-control" placeholder="Search packages..." v-model="searchQuery" />
            <i class="material-symbols-outlined position-absolute top-50 start-0 translate-middle-y">search</i>
          </form>
          <RouterLink to="/packages/create" v-if="isSuperAdmin" class="btn btn-outline-primary py-1 px-2 px-sm-4 fs-14 fw-medium rounded-3 hover-bg">
            <span class="py-sm-1 d-block">
              <i class="ri-add-line d-none d-sm-inline-block me-1"></i>
              <span>Add New Package</span>
            </span>
          </RouterLink>
        </div>

        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Package Name</th>
                  <th scope="col">Price</th>
                  <th scope="col">Status</th>
                  <th scope="col">Services</th>
                  <th scope="col" v-if="isSuperAdmin">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="6" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="filteredPackages.length === 0">
                  <td colspan="6" class="text-center py-4">No packages found.</td>
                </tr>
                <tr v-else v-for="pkg in filteredPackages" :key="pkg.package_id">
                  <td>#{{ pkg.package_id }}</td>
                  <td>
                    <h6 class="mb-0 fs-14 fw-medium">{{ pkg.package_name }}</h6>
                  </td>
                  <td class="text-body">Rp {{ Number(pkg.price).toLocaleString('id-ID') }}</td>
                  <td>
                    <span class="badge bg-opacity-10 p-2 fs-12 fw-normal" :class="pkg.is_active ? 'bg-success text-success' : 'bg-danger text-danger'">
                      {{ pkg.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td>
                    <span v-for="srv in pkg.services" :key="srv.service_id" class="badge bg-secondary me-1">
                      {{ srv.service_name }}
                    </span>
                  </td>
                  <td v-if="isSuperAdmin">
                    <div class="d-flex align-items-center gap-1">
                      <RouterLink :to="`/packages/${pkg.package_id}/edit`" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-body">edit</i>
                      </RouterLink>
                      <button @click="deletePackage(pkg.package_id)" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-danger">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import PageTitle from '@/components/Common/PageTitle.vue';
import packageService from '@/services/packageService';

export default defineComponent({
  name: 'PackageListPage',
  components: {
    PageTitle
  },
  setup() {
    const packages = ref<any[]>([]);
    const loading = ref(true);
    const searchQuery = ref('');

    const isSuperAdmin = computed(() => {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      try {
        const user = JSON.parse(userStr);
        return user.role === 'super_admin';
      } catch (e) {
        return false;
      }
    });

    const fetchPackages = async () => {
      loading.value = true;
      try {
        const response = await packageService.getAll();
        packages.value = response.data;
      } catch (error) {
        console.error('Error fetching packages:', error);
      } finally {
        loading.value = false;
      }
    };

    const deletePackage = async (id: number) => {
      if (confirm('Are you sure you want to delete this package?')) {
        try {
          await packageService.delete(id);
          fetchPackages();
        } catch (error) {
          console.error('Error deleting package:', error);
        }
      }
    };

    const filteredPackages = computed(() => {
      if (!searchQuery.value) return packages.value;
      const q = searchQuery.value.toLowerCase();
      return packages.value.filter(p => p.package_name.toLowerCase().includes(q));
    });

    onMounted(() => {
      fetchPackages();
    });

    return {
      packages,
      loading,
      searchQuery,
      filteredPackages,
      deletePackage,
      isSuperAdmin
    };
  }
});
</script>
